import type { Modifier, Obj } from "@popperjs/core";
import { createPopper } from "@popperjs/core";
import type { Placement } from "@popperjs/core/lib/enums";
import type { OffsetsFunction } from "@popperjs/core/lib/modifiers/offset";
import type { Ref } from "vue";
import { computed, nextTick, onScopeDispose, ref, watch } from "vue";

import { useUserStore } from "@/store/user";
import { useZoomStore } from "@/store/zoom";

export interface ActionMenuConfig {
  placement: Placement;
  offset: [number, number] | OffsetsFunction;
}

interface Props {
  referenceEl: Ref<Element | null>;
  open: Ref<boolean>;
  disabled: Ref<Readonly<boolean>>;
  config: Ref<ActionMenuConfig>;
}

export function useActionMenu({ referenceEl, open, disabled, config }: Props) {
  const isOpen = computed(
    () =>
      !!referenceEl.value &&
      open.value &&
      !disabled.value &&
      !useZoomStore().zooming &&
      useUserStore().isAllowed("edit"),
  );

  const popperEl = ref<HTMLElement>();
  const popper = ref<ReturnType<typeof createPopper> | null>(null);
  let resizeObserver: ResizeObserver | null = null;

  watch(isOpen, () => {
    if (isOpen.value) {
      void newPopper();
    } else {
      destroyPopper();
    }
  });

  watch(config, () => {
    void popper.value?.setOptions(popperOptions(config));
  });

  watch(referenceEl, async () => {
    await nextTick();
    destroyPopper();
    void newPopper();
  });

  async function newPopper() {
    if (popper.value) return;

    await nextTick();

    if (!referenceEl.value || !popperEl.value) return;

    popper.value = createPopper(
      referenceEl.value,
      popperEl.value,
      popperOptions(config, { name: "flip", enabled: false }),
    );

    // Re-anchor the menu when the reference's box changes (e.g. layout reflow
    // triggered by a fullscreen modal closing). Popper's default listeners only
    // cover window scroll/resize, which can fire before Vue re-applies styles.
    resizeObserver = new ResizeObserver(() => void popper.value?.update());
    resizeObserver.observe(referenceEl.value);
  }

  function popperOptions(
    config: Ref<ActionMenuConfig>,
    ...modifiers: Array<Partial<Modifier<unknown, Obj>>>
  ) {
    return {
      placement: config.value.placement,
      modifiers: [{ name: "offset", options: { offset: config.value.offset } }, ...modifiers],
    };
  }

  function destroyPopper() {
    resizeObserver?.disconnect();
    resizeObserver = null;
    popper.value?.destroy();
    popper.value = null;
  }

  // Vue auto-disposes watchers on unmount, but the popper/observer are not
  // tied to a watcher — release them explicitly to avoid leaks if the host
  // component unmounts while the menu is open.
  onScopeDispose(destroyPopper);

  function updateRef(ref: HTMLElement) {
    popperEl.value = ref;
  }

  function updateMenu() {
    void popper.value?.update();
  }

  function isInMenu(node: Node) {
    return popperEl.value?.contains(node);
  }

  return { isOpen, isInMenu, updateMenu, updateRef };
}
